export enum AccessibilityLevel {
  A = "A",
  AA = "AA",
  AAA = "AAA",
}

const ACCESSIBILITY_LEVEL_MIN_RATIOS = {
  [AccessibilityLevel.A]: 3,
  [AccessibilityLevel.AA]: 4.5,
  [AccessibilityLevel.AAA]: 7,
} as const satisfies Record<AccessibilityLevel, number>

/** Text color for use on dark backgrounds. */
export const LIGHT_TEXT_COLOR = "#ffffff"
/** Text color for use on light backgrounds (matches colors.secondaryDark). */
export const DARK_TEXT_COLOR = "#223358"
/**
 * WCAG relative luminance above which a background is considered "light".
 * Luminance 0.4 ≈ the point where white text drops below WCAG Level A (3:1).
 */
const LIGHT_LUMINANCE_THRESHOLD = 0.4

export default {
  hexToRgba(hex: string, alpha = 1) {
    if (hex.match(/^rgba/)) return hex

    const rgbCodes = hex.replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (m, r, g, b) => `#${r}${r}${g}${g}${b}${b}`
    )

    if (rgbCodes) {
      const values = rgbCodes.substring(1).match(/.{2}/g)
      if (values) {
        const rgbArray = values.map((x) => parseInt(x, 16))
        return `rgba(${rgbArray[0]},${rgbArray[1]},${rgbArray[2]},${alpha})`
      }
      throw new Error("hex passed is not a valid rgba")
    }

    throw new Error("hex passed is not a valid rgba")
  },

  rgbToHex(r: number, g: number, b: number) {
    const hexValues = [r, g, b]
      .map((x) => {
        const hex = x.toString(16)
        return hex.length === 1 ? `0${hex}` : hex
      })
      .join("")

    return `#${hexValues}`
  },

  /**
   * Convert hex color to RGB values
   */
  hexToRgb(hex: string): { r: number; g: number; b: number } {
    const rgbString = this.hexToRgba(hex)
    const match = rgbString.match(/rgba?\((\d+),(\d+),(\d+)(?:,\d*\.?\d+)?\)/)
    if (!match || !match[1] || !match[2] || !match[3]) {
      throw new Error("Invalid RGB(A) string from hexToRgba")
    }

    return {
      r: parseInt(match[1], 10),
      g: parseInt(match[2], 10),
      b: parseInt(match[3], 10),
    }
  },

  /**
   * Calculate relative luminance of a color
   * Based on WCAG 2.1 guidelines: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
   */
  getRelativeLuminance(hex: string): number {
    const { r, g, b } = this.hexToRgb(hex)

    // Convert to sRGB and calculate relative luminance
    const calculateSRGB = (channel: number) => {
      const c = channel / 255
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    }

    const rs = calculateSRGB(r)
    const gs = calculateSRGB(g)
    const bs = calculateSRGB(b)

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
  },

  /**
   * Calculate the contrast ratio between two colors
   * Returns a value between 1 and 21 (higher is better contrast)
   */
  getContrastRatio(color1: string, color2: string): number {
    const lum1 = this.getRelativeLuminance(color1)
    const lum2 = this.getRelativeLuminance(color2)

    const lighter = Math.max(lum1, lum2)
    const darker = Math.min(lum1, lum2)

    return (lighter + 0.05) / (darker + 0.05)
  },

  /**
   * Test if two colors meet WCAG accessibility standards
   */
  isAccessible({
    backgroundColor,
    textColor,
    level = AccessibilityLevel.A,
  }: {
    backgroundColor: string
    textColor: string
    level?: AccessibilityLevel
  }): boolean {
    const contrastRatio = this.getContrastRatio(backgroundColor, textColor)
    const minRatio = ACCESSIBILITY_LEVEL_MIN_RATIOS[level]
    return contrastRatio >= minRatio
  },

  /**
   * Adjust a background color to be accessible with text color
   * This function darkens or lightens the color until it meets WCAG AA standards
   */
  makeBackgroundColorAccessible({
    backgroundColor,
    textColor,
    fallbackColor = "#424242",
    level = AccessibilityLevel.A,
  }: {
    backgroundColor: string
    textColor: string
    fallbackColor?: string
    level?: AccessibilityLevel
  }): string {
    // If already accessible, return as-is
    if (this.isAccessible({ backgroundColor, textColor, level })) {
      return backgroundColor
    }

    const { r, g, b } = this.hexToRgb(backgroundColor)
    const minRatio = ACCESSIBILITY_LEVEL_MIN_RATIOS[level]

    // Try darkening first (more common case for backgrounds)
    let adjustedColor = backgroundColor
    let factor = 0.9

    while (factor > 0.1) {
      const newR = Math.round(r * factor)
      const newG = Math.round(g * factor)
      const newB = Math.round(b * factor)

      adjustedColor = this.rgbToHex(newR, newG, newB)

      if (this.getContrastRatio(adjustedColor, textColor) >= minRatio) {
        return adjustedColor
      }

      factor -= 0.05
    }

    // If darkening didn't work, try lightening
    factor = 1.1
    while (factor < 2.0) {
      const newR = Math.min(255, Math.round(r * factor))
      const newG = Math.min(255, Math.round(g * factor))
      const newB = Math.min(255, Math.round(b * factor))

      adjustedColor = this.rgbToHex(newR, newG, newB)

      if (this.getContrastRatio(adjustedColor, textColor) >= minRatio) {
        return adjustedColor
      }

      factor += 0.05
    }

    return fallbackColor
  },

  /**
   * Returns "#ffffff" for dark backgrounds or "#223358" (secondaryDark) for light
   * backgrounds based on WCAG 2.1 relative luminance. The threshold (0.4) is the
   * point where white text drops below WCAG Level A contrast (3:1).
   *
   * The same algorithm is implemented in Go as invoices.TextColorForBackground.
   */
  textColorForBackground(hex: string): string {
    const lum = this.getRelativeLuminance(hex)
    return lum > LIGHT_LUMINANCE_THRESHOLD ? DARK_TEXT_COLOR : LIGHT_TEXT_COLOR
  },

  /**
   * Compute a slightly darker variant of a background color by alpha-blending
   * with secondary (#32497f) at 15% opacity. Used for the frosted-glass pill
   * on dark backgrounds (white text).
   *
   * The same algorithm is implemented in Go as invoices.DarkenColor.
   */
  darkenColor(hex: string): string {
    const { r, g, b } = this.hexToRgb(hex)

    const alpha = 0.15
    const oR = 0x32
    const oG = 0x49
    const oB = 0x7f

    return this.rgbToHex(
      Math.round(r * (1 - alpha) + oR * alpha),
      Math.round(g * (1 - alpha) + oG * alpha),
      Math.round(b * (1 - alpha) + oB * alpha)
    )
  },

  /**
   * Compute a slightly lighter variant of a background color by alpha-blending
   * with white (#ffffff) at 15% opacity. Used for the frosted-glass pill
   * on light backgrounds (dark text).
   *
   * The same algorithm is implemented in Go as invoices.LightenColor.
   */
  lightenColor(hex: string): string {
    const { r, g, b } = this.hexToRgb(hex)

    const alpha = 0.15

    return this.rgbToHex(
      Math.round(r * (1 - alpha) + 0xff * alpha),
      Math.round(g * (1 - alpha) + 0xff * alpha),
      Math.round(b * (1 - alpha) + 0xff * alpha)
    )
  },

  /**
   * Returns a frosted-glass background color for the given hex color.
   * On dark backgrounds (white text) it darkens; on light backgrounds (dark text)
   * it lightens. Matches Go invoices.FrostedColor.
   */
  frostedColor(hex: string): string {
    return this.textColorForBackground(hex) === DARK_TEXT_COLOR
      ? this.lightenColor(hex)
      : this.darkenColor(hex)
  },
}
